import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Close from '@mui/icons-material/Close';
import { useAuth } from '../contexts/AuthContext';
import {
  PWA_INSTALL_DISMISSED_KEY,
  PWA_INSTALL_ELIGIBLE_KEY,
  PWA_INSTALL_NEVER_KEY,
  PWA_MAIN_SCREEN_PATHS,
  PwaBeforeInstallPromptEvent,
} from '../constants/pwaInstallPrompt';

const PWA_INSTALL_AUTO_HIDE_MS = 10_000;

const PwaInstallPrompt: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { pathname } = useLocation();
  const isMainScreen = PWA_MAIN_SCREEN_PATHS.includes(pathname);

  const [deferredPrompt, setDeferredPrompt] = useState<PwaBeforeInstallPromptEvent | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [installSnackbarProgress, setInstallSnackbarProgress] = useState(100);

  const shouldShowInstallPrompt = useCallback((): boolean => {
    if (localStorage.getItem(PWA_INSTALL_NEVER_KEY) === '1') {
      return false;
    }
    if (sessionStorage.getItem(PWA_INSTALL_DISMISSED_KEY) === '1') {
      return false;
    }
    return true;
  }, []);

  const dismissInstallPromptLater = useCallback(() => {
    sessionStorage.setItem(PWA_INSTALL_DISMISSED_KEY, '1');
    setSnackbarOpen(false);
  }, []);

  const dismissInstallPromptNever = useCallback(() => {
    localStorage.setItem(PWA_INSTALL_NEVER_KEY, '1');
    setSnackbarOpen(false);
  }, []);

  const tryOpenInstallSnackbar = useCallback(() => {
    if (!deferredPrompt || !isAuthenticated || !isMainScreen) {
      return;
    }
    if (!sessionStorage.getItem(PWA_INSTALL_ELIGIBLE_KEY)) {
      return;
    }
    if (!shouldShowInstallPrompt()) {
      return;
    }
    sessionStorage.removeItem(PWA_INSTALL_ELIGIBLE_KEY);
    setSnackbarOpen(true);
  }, [deferredPrompt, isAuthenticated, isMainScreen, shouldShowInstallPrompt]);

  useEffect(() => {
    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setDeferredPrompt(event as PwaBeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  useEffect(() => {
    tryOpenInstallSnackbar();
  }, [tryOpenInstallSnackbar]);

  useEffect(() => {
    if (snackbarOpen && (!isAuthenticated || !isMainScreen)) {
      setSnackbarOpen(false);
    }
  }, [snackbarOpen, isAuthenticated, isMainScreen]);

  useEffect(() => {
    if (!snackbarOpen) {
      setInstallSnackbarProgress(100);
      return;
    }

    const start = Date.now();
    const intervalId = window.setInterval(() => {
      const elapsed = Date.now() - start;
      const remainingPct = Math.max(
        0,
        100 - (elapsed / PWA_INSTALL_AUTO_HIDE_MS) * 100
      );
      setInstallSnackbarProgress(remainingPct);

      if (elapsed >= PWA_INSTALL_AUTO_HIDE_MS) {
        window.clearInterval(intervalId);
        dismissInstallPromptLater();
      }
    }, 50);

    return () => window.clearInterval(intervalId);
  }, [snackbarOpen, dismissInstallPromptLater]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      return;
    }
    deferredPrompt.prompt();
    const choiceResult = await deferredPrompt.userChoice;
    console.log(`User response to the install prompt: ${choiceResult.outcome}`);
    setDeferredPrompt(null);
    setSnackbarOpen(false);
  };

  return (
    <Snackbar
      open={snackbarOpen}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      onClose={(_event, reason) => {
        if (reason === 'clickaway') {
          return;
        }
      }}
    >
      <Paper
        elevation={6}
        sx={{
          width: '100%',
          maxWidth: { xs: 'calc(100vw - 32px)', sm: 420 },
          overflow: 'hidden',
          bgcolor: (theme) =>
            theme.palette.mode === 'light' ? '#323232' : '#ededed',
          color: (theme) =>
            theme.palette.mode === 'light' ? '#fff' : 'rgba(0, 0, 0, 0.87)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            px: 2,
            pt: 1.5,
            pb: 1,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              ¿Querés instalar eOne en tu dispositivo?
            </Typography>
            <Typography
              variant="body2"
              sx={{ opacity: 0.85, mt: 0.25, lineHeight: 1.4 }}
            >
            </Typography>
          </Box>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
            <Button color="secondary" size="small" onClick={handleInstallClick}>
              Instalar
            </Button>
            <IconButton
              size="small"
              aria-label="Cerrar"
              color="inherit"
              onClick={dismissInstallPromptLater}
            >
              <Close fontSize="small" />
            </IconButton>
          </Stack>
        </Box>
        <Box sx={{ px: 1, pb: 0.5 }}>
          <Button
            fullWidth
            size="small"
            color="inherit"
            onClick={dismissInstallPromptNever}
            sx={(theme) => ({
              justifyContent: 'flex-start',
              textTransform: 'none',
              fontSize: '0.75rem',
              opacity: 0.8,
              py: 0.25,
              minHeight: 28,
              '&:hover': {
                opacity: 1,
                bgcolor:
                  theme.palette.mode === 'light'
                    ? 'rgba(255,255,255,0.08)'
                    : 'rgba(0,0,0,0.06)',
              },
            })}
          >
            No volver a mostrar
          </Button>
        </Box>
        <LinearProgress
          variant="determinate"
          value={installSnackbarProgress}
          sx={(theme) => ({
            height: 3,
            bgcolor:
              theme.palette.mode === 'light'
                ? 'rgba(255,255,255,0.12)'
                : 'rgba(0,0,0,0.08)',
            '& .MuiLinearProgress-bar': {
              bgcolor:
                theme.palette.mode === 'light'
                  ? 'rgba(255,255,255,0.45)'
                  : 'rgba(0,0,0,0.28)',
            },
          })}
        />
      </Paper>
    </Snackbar>
  );
};

export default PwaInstallPrompt;
